import { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { PaginatedResponse } from '../../types/AssessmentTable.type';
import { apiGet } from '../../utils/apiClient';
import { CourseLevelLog } from '../../types/CourseStageAccordion.type';
import { rowHoverBg, selectedRowBg } from '../../utils/rowHighlight';
import TableLoader from '../common/TableLoader';

type CourseLevelLogDialogProps = {
  open: boolean;
  handleClose: () => void;
  registrationId: number | null;
};

const CourseLevelLogDialog = ({
  open,
  handleClose,
  registrationId,
}: CourseLevelLogDialogProps) => {
  const [courseLevelLog, setCourseLevelLog] = useState<CourseLevelLog[]>([]);
  const [loading, setLoading] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [filterDate, setFilterDate] = useState<Dayjs | null>(null);

  // Highlighted row
  const [highlightedRow, setHighlightedRow] = useState<string | null>(null);

  const fetchCourseLevelLog = async () => {
    if (registrationId) {
      try {
        setLoading(true);
        const response = await apiGet<PaginatedResponse<CourseLevelLog>>(
          `/course/get-course-level-logs/${registrationId}?page=${page + 1}&limit=${limit}&date=${
            filterDate && filterDate.isValid()
              ? filterDate.format('YYYY-MM-DD')
              : ''
          }`,
        );
        setCourseLevelLog(response.data);
        setTotalItems(response.metadata.totalItems);
      } catch (error) {
        console.error('Error fetching course level Log:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseDialog = () => {
    handleClose();
    setCourseLevelLog([]);
    setPage(0);
    setLimit(10);
    setTotalItems(0);
    setFilterDate(null);
  };

  useEffect(() => {
    if (open) {
      fetchCourseLevelLog();
    }
  }, [open, registrationId, page, limit, filterDate]);

  return (
    <Dialog fullWidth open={open} onClose={handleCloseDialog}>
      <DialogTitle>Promote / Demote History</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            my: 2,
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'center',
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="DD/MM/YYYY"
              label="Filter by Action Date"
              value={filterDate}
              onChange={(newDate) => setFilterDate(newDate)}
              slotProps={{
                field: {
                  clearable: true,
                  onClear: () => setFilterDate(null),
                },
              }}
            />
          </LocalizationProvider>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#4da7db' }}>
                <TableCell>
                  <Typography fontWeight="bold" color="#fff">
                    Date
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold" color="#fff">
                    Time
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold" color="#fff">
                    Coach
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold" color="#fff">
                    Action
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoader colSpan={4} />
              ) : (
                <>
              {courseLevelLog.map((item) => (
                <TableRow
                  key={item.id}
                  sx={{
                    '&:hover': { backgroundColor: rowHoverBg },
                    backgroundColor:
                      highlightedRow === `data_${item.id}`
                        ? selectedRowBg
                        : 'inherit',
                  }}
                  onClick={() => {
                    if (highlightedRow === `data_${item.id}`) {
                      setHighlightedRow(null);
                    } else {
                      setHighlightedRow(`data_${item.id}`);
                    }
                  }}
                >
                  <TableCell>
                    {new Date(item.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {new Date(item.createdAt).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })}
                  </TableCell>
                  <TableCell>{item.coach.name}</TableCell>
                  <TableCell>{item.comment}</TableCell>
                </TableRow>
              ))}
              {courseLevelLog.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No history found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalItems}
          rowsPerPage={limit}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CourseLevelLogDialog;
